import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Loader2, Search, EyeOff, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import DeskConfirm from "@/components/crm/DeskConfirm";
import api from "@/lib/api";
import RenameMemoryValueDialog from "@/components/documents/RenameMemoryValueDialog";

// ---------------------------------------------------------------------------
// ManageMemoryValuesDialog — spec iter151 (Feb 2026).
//
// Modale de gestion en masse des valeurs mémorisées d'un champ
// « Texte (mémoire) » (accessible depuis la fiche Type de document).
// Liste toutes les valeurs distinctes déjà saisies dans les documents
// de ce type pour ce champ, avec un bouton « Renommer » par entrée.
//
// Chaque renommage utilise le dialog RenameMemoryValueDialog (compteur
// d'impact + fusion silencieuse en cas de collision).
//
// Props :
//   • open, onOpenChange : contrôle du Dialog
//   • typeId             : id du DocumentType
//   • fieldKey           : clé du champ
//   • fieldLabel         : libellé du champ (affichage)
// ---------------------------------------------------------------------------
export default function ManageMemoryValuesDialog({
  open,
  onOpenChange,
  typeId,
  basePath = null,
  fieldKey,
  fieldLabel,
}) {
  const base = basePath || `/document-types/${typeId}`;
  const scope = typeId || basePath;
  const [values, setValues] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [renaming, setRenaming] = useState(null);  // string | null

  const load = useCallback(async () => {
    if (!scope || !fieldKey) return;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await api.get(`${base}/field-memory`, {
        params: { field_key: fieldKey },
      });
      setValues(r.data?.values || []);
      setExcluded(r.data?.excluded || []);
    } catch (_) {
      setValues([]);
      setExcluded([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [typeId, fieldKey]);

  useEffect(() => {
    if (open) { setQ(""); load(); }
  }, [open, load]);

  const act = async (kind, v) => {
    setBusy(v);
    try {
      await api.post(`${base}/field-memory/${kind}`, { field_key: fieldKey, value: v });
      toast.success(kind === "restore" ? `« ${v} » réactivée dans les suggestions` : `« ${v} » supprimée de la mémoire`);
      load();
    } catch (err) {
      toast.error("Action impossible", { description: err.response?.data?.detail || "" });
    } finally {
      setBusy(null);
      setConfirmDel(null);
    }
  };

  const filtered = q.trim()
    ? values.filter((v) => v.toLowerCase().includes(q.trim().toLowerCase()))
    : values;

  return (
    <>
      <Dialog open={!!open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="manage-memory-values-dialog"
          style={{ background: "#1B1B1B", border: "1px solid #2A2A2A" }}
        >
          <DialogHeader>
            <DialogTitle className="text-base">
              Valeurs mémorisées — {fieldLabel || fieldKey}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            <div className="text-xs text-muted-foreground">
              Renommer une valeur met à jour <b>tous les documents</b>{" "}
              existants qui l'utilisent.
            </div>

            <div className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher une valeur…"
                className="pl-8"
                data-testid="manage-memory-search"
                style={{ background: "#141414", border: "1px solid #2A2A2A" }}
              />
            </div>

            <div
              className="rounded-md overflow-hidden max-h-72 overflow-y-auto"
              style={{ background: "#141414", border: "1px solid #2A2A2A" }}
            >
              {loading ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
                  <Loader2 size={13} className="animate-spin" /> Chargement…
                </div>
              ) : loadError ? (
                <div
                  className="px-3 py-6 text-center text-xs"
                  style={{ color: "#EF4444" }}
                  data-testid="manage-memory-error"
                >
                  Impossible de charger les valeurs. Réessayer plus tard.
                </div>
              ) : filtered.length === 0 ? (
                <div
                  className="px-3 py-6 text-center text-xs text-muted-foreground"
                  data-testid="manage-memory-empty"
                >
                  {values.length === 0
                    ? "Aucune valeur mémorisée pour l'instant."
                    : "Aucun résultat pour cette recherche."}
                </div>
              ) : (
                filtered.map((v) => (
                  <div
                    key={v}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
                    data-testid="manage-memory-value-row"
                  >
                    <span style={{ color: "#EDEDED" }}>{v}</span>
                    <button
                      type="button"
                      onClick={() => setRenaming(v)}
                      className="h-7 px-2 rounded-md inline-flex items-center gap-1 text-[11px] transition-colors"
                      style={{ color: "#5A9EE0", background: "rgba(43,87,154,0.10)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(43,87,154,0.20)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(43,87,154,0.10)")}
                      data-testid="manage-memory-rename-btn"
                      title="Renommer cette valeur"
                    >
                      <Pencil size={12} strokeWidth={2.2} /> Renommer
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Spec iter301 — Valeurs masquées (exclues des suggestions). */}
            {excluded.length > 0 && (
              <div className="pt-1" data-testid="manage-memory-excluded-section">
                <div className="flex items-center gap-2 mb-1.5">
                  <EyeOff size={12} className="text-muted-foreground" />
                  <span className="text-[10.5px] uppercase tracking-widest text-muted-foreground">
                    Valeurs masquées · {excluded.length}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground mb-2">
                  Présentes dans d'anciens documents mais non suggérées. Supprimer retire uniquement la
                  suggestion future — les documents existants ne sont jamais modifiés.
                </div>
                <div className="rounded-md overflow-hidden max-h-44 overflow-y-auto"
                  style={{ background: "#141414", border: "1px dashed #2A2A2A" }}>
                  {excluded.map((v) => (
                    <div key={v} className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
                      data-testid="manage-memory-excluded-row">
                      <span className="truncate" style={{ color: "#7a7a80" }}>{v}</span>
                      <span className="inline-flex items-center gap-1.5 shrink-0">
                        <button type="button" disabled={busy === v} onClick={() => act("restore", v)}
                          className="h-7 px-2 rounded-md inline-flex items-center gap-1 text-[11px] transition-colors disabled:opacity-50"
                          style={{ color: "#22C55E", background: "rgba(34,197,94,0.10)" }}
                          data-testid="manage-memory-restore-btn" title="Remettre dans les suggestions">
                          <RotateCcw size={12} strokeWidth={2.2} /> Réactiver
                        </button>
                        <button type="button" disabled={busy === v} onClick={() => setConfirmDel(v)}
                          className="h-7 w-7 rounded-md inline-flex items-center justify-center transition-colors disabled:opacity-50"
                          style={{ color: "#EF4444", background: "rgba(239,68,68,0.10)" }}
                          data-testid="manage-memory-delete-btn" title="Supprimer de la mémoire">
                          <Trash2 size={12} strokeWidth={2.2} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              data-testid="manage-memory-close-btn"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeskConfirm open={!!confirmDel} title={`Supprimer « ${confirmDel} » de la mémoire ?`}
        message={`La valeur ne sera plus jamais suggérée pour « ${fieldLabel || fieldKey} ». Les documents existants qui la contiennent ne sont pas modifiés.`}
        confirmLabel="Supprimer" onClose={() => setConfirmDel(null)} onConfirm={() => act("forget", confirmDel)} testId="manage-memory-delete-confirm" />

      <RenameMemoryValueDialog
        open={!!renaming}
        onOpenChange={(v) => !v && setRenaming(null)}
        typeId={typeId}
        basePath={base}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel}
        oldValue={renaming || ""}
        existingValues={values}
        onDone={load}
      />
    </>
  );
}
